import { makeVar, ReactiveVar, useReactiveVar } from '@apollo/client';
import { removeElement } from '@common/neo.tax';
import { uuid4 } from '@sentry/utils';
import { DEFAULT_TOAST_DURATION, DEFAULT_TOAST_ERROR_DURATION, ToastProps } from '@ui-components/neo.tax';

import { reactiveVarOrchestrator } from '../orchestrators';

export type ToastConfigsVar = ReactiveVar<ToastProps[]>;

// Don't export; use provided hooks or functions to get/set values
const toastConfigsVar: ToastConfigsVar = makeVar<ToastProps[]>([]);

export const useToastConfigs = (): ToastProps[] => useReactiveVar(toastConfigsVar);

export const pushToast = (options: Omit<ToastProps, 'id'> & { id?: string }): ToastProps => {
  const currentToastConfigs = toastConfigsVar();
  const id = options.id || uuid4();
  const newToastConfig: ToastProps = {
    id,
    severity: 'info',
    duration: options.severity === 'error' ? DEFAULT_TOAST_ERROR_DURATION : DEFAULT_TOAST_DURATION,
    onClose: () => removeToast(id),
    ...options,
  };
  const newToastConfigs = [
    ...currentToastConfigs.filter((toastConfig) => toastConfig.id !== toastConfig.id),
    newToastConfig,
  ];
  toastConfigsVar(newToastConfigs);
  return newToastConfig;
};

export const pushErrorToast = (
  title: string,
  options: Omit<ToastProps, 'id' | 'title' | 'severity'> & { id?: string } = {},
): ToastProps =>
  pushToast({
    title,
    severity: 'error',
    ...options,
  });

export const pushInfoToast = (
  title: string,
  options: Omit<ToastProps, 'id' | 'title' | 'severity'> & { id?: string } = {},
): ToastProps =>
  pushToast({
    title,
    severity: 'info',
    ...options,
  });

export const pushSuccessToast = (
  title: string,
  options: Omit<ToastProps, 'id' | 'title' | 'severity'> & { id?: string } = {},
): ToastProps =>
  pushToast({
    title,
    severity: 'success',
    ...options,
  });

export const removeToast = (toastId: string): ToastProps | null => {
  const currentToastConfigs = toastConfigsVar();
  const toastToPopIndex = currentToastConfigs.findIndex((toastConfig) => toastConfig.id === toastId);

  if (toastToPopIndex === -1) {
    return null;
  }

  const toastToPop = currentToastConfigs[toastToPopIndex];
  const newToastConfigs = removeElement(currentToastConfigs, toastToPopIndex);
  toastConfigsVar(newToastConfigs);

  return toastToPop;
};

export const clearToasts = (): void => {
  toastConfigsVar([]);
};

reactiveVarOrchestrator.registerClearFunction(clearToasts);
